import React from 'react';
import { Scale, FileText, AlertCircle, ArrowLeft } from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const TermsConditionsPage: React.FC = () => {
  const { navigateTo } = useApp();

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-8">
      {/* Header Container */}
      <div className="max-w-4xl mx-auto space-y-3">
        <button
          onClick={() => navigateTo('home')}
          className="text-xs text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 font-semibold cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Homepage</span>
        </button>

        <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-xl space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <Scale className="w-3.5 h-3.5 text-emerald-400" />
            <span>Marketplace Terms of Service</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
            Terms & Conditions
          </h1>
          <p className="text-xs sm:text-sm text-slate-300">
            Last updated: September 2024. Governed by the Laws of the Federal Republic of Nigeria.
          </p>
        </div>
      </div>

      {/* Legal Advisory Notice */}
      <div className="max-w-4xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs text-amber-900 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          <strong>Legal Disclaimer:</strong> These Terms and Conditions outline the contractual basis under which
          customers and independent professionals access the JobMatch NG marketplace. They are provided for
          platform transparency and should be reviewed by qualified Nigerian legal counsel.
        </p>
      </div>

      {/* Main Legal Content */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-12 border border-slate-200 shadow-xs space-y-8 text-xs sm:text-sm text-slate-700 leading-relaxed">
        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">1.</span> Acceptance of Terms
          </h2>
          <p>
            By creating an account, browsing the directory, posting a repair request, or bidding on a job
            through JobMatch NG (“the Platform”), you confirm that you have read, understood, and agree to be
            legally bound by these Terms and Conditions. If you do not accept these terms, you must discontinue
            use of the Platform immediately.
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">2.</span> Nature of JobMatch NG Marketplace
          </h2>
          <p>
            JobMatch NG operates strictly as a <strong>communications and technology facilitator</strong> connecting
            independent clients (“Customers”) with independent skilled service providers (“Professionals”).
          </p>
          <p>
            <strong>Crucial Clarification:</strong> JobMatch NG is <em>not</em> an employer, general contractor,
            agent, or guarantor of any professional. Professionals on the platform are self-employed independent contractors.
            The actual contract for trade work, physical execution, materials purchase, and job compensation is entered
            into solely and directly between the Customer and the selected Professional.
          </p>
        </section>

        {/* Section 3 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">3.</span> Account Registration & Eligibility
          </h2>
          <p>To use JobMatch NG, you must:</p>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li>Be at least 18 years of age with full legal capacity under Nigerian law.</li>
            <li>Provide accurate, truthful, and up-to-date registration details (legal name, phone, address).</li>
            <li>Maintain the confidentiality of your login credentials and accept responsibility for account activities.</li>
            <li>Immediately notify JobMatch NG of any unauthorized access or security breach.</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">4.</span> Customer Responsibilities
          </h2>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li>Provide accurate and detailed descriptions of the repair, installation, or construction required.</li>
            <li>Ensure safe and reasonable physical access to the premises where trade work is to be performed.</li>
            <li>Confirm all quotations, scope of work, and material costs in writing prior to work commencement.</li>
            <li>Pay the agreed sum directly to the professional in accordance with their agreed payment schedule.</li>
            <li>Inspect completed work with the professional and submit honest, constructive reviews.</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">5.</span> Professional Responsibilities
          </h2>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li>Accurately represent your technical certifications, skills, trade experience, and service areas.</li>
            <li>Submit genuine, unexpired Nigerian identification documents (NIN, Driver’s License, etc.) during verification.</li>
            <li>Submit transparent quotes detailing estimated labor fees and material costs in Naira.</li>
            <li>Perform all agreed physical work in a professional, workmanlike manner adhering to sound safety codes.</li>
            <li>Maintain clear, respectful communication with customers throughout the engagement.</li>
          </ul>
        </section>

        {/* Section 6 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">6.</span> Verification Framework & Limitations
          </h2>
          <p>
            JobMatch NG administers a multi-step verification process (Government ID, utility document, location,
            facial liveness). Verification is intended to confirm that the professional’s presented credentials match
            public records and identity benchmarks.
          </p>
          <p>
            <strong>Limitation:</strong> Verification does <em>not</em> constitute an endorsement, insurance policy,
            or guarantee of a professional’s personal honesty, temperamental suitability, or absence of error. Customers
            remain responsible for supervising work performed on their properties.
          </p>
        </section>

        {/* Section 7 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">7.</span> Marketplace Commission & Escrow Billing Terms
          </h2>
          <p>
            Customers may post jobs and compare quotations free of platform charges.
          </p>
          <p>
            Professionals can register, undergo verification, and submit job proposals free of monthly subscription charges (₦0 monthly fee).
            When a customer accepts a professional's quote and makes payment via Paystack, JobMatch NG deducts a standard platform commission (default 10%), with the remaining 90% disbursed directly to the professional upon verified job completion.
          </p>
        </section>

        {/* Section 8 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">8.</span> Limitation of Liability
          </h2>
          <p>
            To the maximum extent permitted by Nigerian law, JobMatch NG, its officers, employees, and agents shall
            not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages,
            including but not limited to loss of property, bodily injury, delays, defective workmanship, or financial
            disputes arising out of or in connection with the physical services rendered by independent professionals.
          </p>
        </section>

        {/* Section 9 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">9.</span> Governing Law & Dispute Resolution
          </h2>
          <p>
            These Terms and Conditions shall be governed by and construed in accordance with the laws of the
            Federal Republic of Nigeria. In the event of a dispute, parties agree to first seek amicable settlement
            through informal mediation before resorting to formal court proceedings in Nigeria.
          </p>
        </section>

        {/* Section 10 */}
        <section className="space-y-3 border-t border-slate-100 pt-6">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">10.</span> Inquiries & Contact
          </h2>
          <p>
            If you have questions regarding these Terms & Conditions, please contact us at{' '}
            <strong className="text-slate-900">support@jobmatch.ng</strong>.
          </p>
        </section>
      </div>
    </div>
  );
};
